/**
 * Character limits for social posts.
 * Keep xWeightedLength in sync with worker/src/services/social/text.ts,
 * which enforces the same limits on save.
 */

export const LINKEDIN_MAX_LENGTH = 3000;
export const X_MAX_LENGTH = 280;
export const BLUESKY_MAX_LENGTH = 300;

// Follows twitter-text v3: most Latin text counts 1 per character, other
// scripts and emoji count 2, and every URL counts as 23.
const URL_PATTERN =
  /(?:https?:\/\/|www\.)[^\s]+|\b[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.(?:com|io|ai|co|net|org|dev|app|so|me|xyz|tech|us|uk|ng)\b(?:\/[^\s]*)?/gi;
const URL_WEIGHT = 23;
const LIGHT_RANGES: Array<[number, number]> = [
  [0, 4351],
  [8192, 8205],
  [8208, 8223],
  [8242, 8247],
];
const EMOJI = /\p{Extended_Pictographic}/u;
// Variation selectors and skin tones belong to the emoji before them
const EMOJI_MODIFIER = /[\ufe0e\ufe0f\u{1f3fb}-\u{1f3ff}]/u;
const ZWJ = "\u200d";
const REGIONAL_INDICATOR = /[\u{1f1e6}-\u{1f1ff}]/u;

function codePointWeight(codePoint: number): number {
  return LIGHT_RANGES.some(([lo, hi]) => codePoint >= lo && codePoint <= hi) ? 1 : 2;
}

export function xWeightedLength(text: string): number {
  let length = 0;
  const withoutUrls = text.normalize("NFC").replace(URL_PATTERN, () => {
    length += URL_WEIGHT;
    return "";
  });

  // An emoji sequence (ZWJ family, flag pair) counts as one emoji: 2
  let joined = false;
  let pendingFlag = false;
  for (const char of withoutUrls) {
    if (char === ZWJ) {
      joined = true;
      continue;
    }
    if (EMOJI_MODIFIER.test(char)) continue;
    if (REGIONAL_INDICATOR.test(char)) {
      if (!pendingFlag) length += 2;
      pendingFlag = !pendingFlag;
      joined = false;
      continue;
    }
    pendingFlag = false;
    const isEmoji = EMOJI.test(char);
    if (!(isEmoji && joined)) length += isEmoji ? 2 : codePointWeight(char.codePointAt(0) ?? 0);
    joined = false;
  }
  return length;
}

// Bluesky counts graphemes (what a reader sees as one character). Intl.Segmenter
// isn't in this project's ES2020 lib types, and old browsers lack it.
type GraphemeSegmenter = new (locale: string, options: { granularity: "grapheme" }) => {
  segment(text: string): Iterable<unknown>;
};
const Segmenter = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;

export function graphemeLength(text: string): number {
  if (!Segmenter) return [...text].length;
  let count = 0;
  for (const _ of new Segmenter("en", { granularity: "grapheme" }).segment(text)) count++;
  return count;
}

export function postLength(platform: "linkedin" | "x" | "bluesky", text: string): { length: number; max: number } {
  const trimmed = text.trim();
  if (platform === "bluesky") return { length: graphemeLength(trimmed), max: BLUESKY_MAX_LENGTH };
  return platform === "x"
    ? { length: xWeightedLength(trimmed), max: X_MAX_LENGTH }
    : { length: trimmed.length, max: LINKEDIN_MAX_LENGTH };
}
